/**
 * Lossless JSON core: tokenizer + recursive-descent parser + serializer.
 *
 * Why not `JSON.parse`/`JSON.stringify`?
 *  - `JSON.parse` silently loses precision on large integers, collapses
 *    duplicate keys, and normalizes `-0` and string escapes. For a *diff* and
 *    *validator* tool that is a correctness bug, not a nicety.
 *  - Native parse errors have inconsistent / missing position info across
 *    browsers.
 *
 * This module is intentionally DOM-free and side-effect-free so it can be unit
 * tested directly and later moved into a Web Worker without changes.
 */

export interface Pos {
  /** 0-based character offset. */
  offset: number;
  /** 1-based line number. */
  line: number;
  /** 1-based column number. */
  column: number;
}

export class JsonSyntaxError extends Error {
  readonly pos: Pos;
  constructor(message: string, pos: Pos) {
    super(message);
    this.name = 'JsonSyntaxError';
    this.pos = pos;
  }
}

export type JsonNode =
  | ObjectNode
  | ArrayNode
  | StringNode
  | NumberNode
  | LiteralNode;

export interface ObjectNode {
  type: 'object';
  members: Member[];
  start: number;
  end: number;
}
export interface Member {
  /** Decoded key string (used for duplicate detection). */
  key: string;
  /** Exact source text of the key, including surrounding quotes. */
  keyRaw: string;
  value: JsonNode;
  keyStart: number;
}
export interface ArrayNode {
  type: 'array';
  elements: JsonNode[];
  start: number;
  end: number;
}
export interface StringNode {
  type: 'string';
  /** Exact source text including quotes and escapes. */
  raw: string;
  value: string;
  start: number;
  end: number;
}
export interface NumberNode {
  type: 'number';
  /** Exact source text of the number literal. */
  raw: string;
  start: number;
  end: number;
}
export interface LiteralNode {
  type: 'literal';
  value: true | false | null;
  raw: 'true' | 'false' | 'null';
  start: number;
  end: number;
}

// --- Position helpers -------------------------------------------------------

/** Convert a character offset into a 1-based line/column position. */
export function offsetToPos(text: string, offset: number): Pos {
  let line = 1;
  let column = 1;
  const limit = Math.min(offset, text.length);
  for (let i = 0; i < limit; i++) {
    if (text.charCodeAt(i) === 10 /* \n */) {
      line++;
      column = 1;
    } else {
      column++;
    }
  }
  return { offset, line, column };
}

// --- Tokenizer + parser -----------------------------------------------------

const WS = new Set([0x20, 0x09, 0x0a, 0x0d]);

class Parser {
  private i = 0;
  constructor(private readonly s: string) {}

  parse(): JsonNode {
    this.skipWs();
    if (this.i >= this.s.length) {
      throw this.err('Unexpected end of input: expected a JSON value');
    }
    const node = this.parseValue();
    this.skipWs();
    if (this.i < this.s.length) {
      throw this.err(`Unexpected trailing character '${this.s[this.i]}'`);
    }
    return node;
  }

  private parseValue(): JsonNode {
    this.skipWs();
    const c = this.s[this.i];
    switch (c) {
      case '{':
        return this.parseObject();
      case '[':
        return this.parseArray();
      case '"':
        return this.parseString();
      case 't':
      case 'f':
      case 'n':
        return this.parseLiteral();
      default:
        if (c === '-' || (c >= '0' && c <= '9')) return this.parseNumber();
        throw this.err(
          this.i >= this.s.length
            ? 'Unexpected end of input: expected a JSON value'
            : `Unexpected character '${c}': expected a JSON value`,
        );
    }
  }

  private parseObject(): ObjectNode {
    const start = this.i;
    this.i++; // consume {
    const members: Member[] = [];
    this.skipWs();
    if (this.s[this.i] === '}') {
      this.i++;
      return { type: 'object', members, start, end: this.i };
    }
    for (;;) {
      this.skipWs();
      if (this.s[this.i] !== '"') {
        throw this.err(
          this.i >= this.s.length
            ? 'Unexpected end of input: expected a property name'
            : `Expected a property name in double quotes, got '${this.s[this.i]}'`,
        );
      }
      const keyStart = this.i;
      const keyNode = this.parseString();
      this.skipWs();
      if (this.s[this.i] !== ':') {
        throw this.err(
          this.i >= this.s.length
            ? "Unexpected end of input: expected ':'"
            : `Expected ':' after property name, got '${this.s[this.i]}'`,
        );
      }
      this.i++; // consume :
      const value = this.parseValue();
      members.push({
        key: keyNode.value,
        keyRaw: keyNode.raw,
        value,
        keyStart,
      });
      this.skipWs();
      const next = this.s[this.i];
      if (next === ',') {
        this.i++;
        continue;
      }
      if (next === '}') {
        this.i++;
        return { type: 'object', members, start, end: this.i };
      }
      throw this.err(
        this.i >= this.s.length
          ? "Unexpected end of input: expected ',' or '}'"
          : `Expected ',' or '}' after property value, got '${next}'`,
      );
    }
  }

  private parseArray(): ArrayNode {
    const start = this.i;
    this.i++; // consume [
    const elements: JsonNode[] = [];
    this.skipWs();
    if (this.s[this.i] === ']') {
      this.i++;
      return { type: 'array', elements, start, end: this.i };
    }
    for (;;) {
      const value = this.parseValue();
      elements.push(value);
      this.skipWs();
      const next = this.s[this.i];
      if (next === ',') {
        this.i++;
        continue;
      }
      if (next === ']') {
        this.i++;
        return { type: 'array', elements, start, end: this.i };
      }
      throw this.err(
        this.i >= this.s.length
          ? "Unexpected end of input: expected ',' or ']'"
          : `Expected ',' or ']' after array element, got '${next}'`,
      );
    }
  }

  private parseString(): StringNode {
    const start = this.i;
    this.i++; // consume opening quote
    let value = '';
    for (;;) {
      if (this.i >= this.s.length) {
        throw this.errAt(start, 'Unterminated string');
      }
      const ch = this.s[this.i];
      const code = this.s.charCodeAt(this.i);
      if (ch === '"') {
        this.i++;
        return {
          type: 'string',
          raw: this.s.slice(start, this.i),
          value,
          start,
          end: this.i,
        };
      }
      if (ch === '\\') {
        this.i++;
        const esc = this.s[this.i];
        switch (esc) {
          case '"': value += '"'; this.i++; break;
          case '\\': value += '\\'; this.i++; break;
          case '/': value += '/'; this.i++; break;
          case 'b': value += '\b'; this.i++; break;
          case 'f': value += '\f'; this.i++; break;
          case 'n': value += '\n'; this.i++; break;
          case 'r': value += '\r'; this.i++; break;
          case 't': value += '\t'; this.i++; break;
          case 'u': {
            const hex = this.s.slice(this.i + 1, this.i + 5);
            if (!/^[0-9a-fA-F]{4}$/.test(hex)) {
              throw this.err('Invalid \\u unicode escape sequence');
            }
            value += String.fromCharCode(parseInt(hex, 16));
            this.i += 5;
            break;
          }
          default:
            throw this.err(`Invalid escape sequence '\\${esc ?? ''}'`);
        }
        continue;
      }
      if (code < 0x20) {
        throw this.err('Control character must be escaped in a string');
      }
      value += ch;
      this.i++;
    }
  }

  private parseNumber(): NumberNode {
    const start = this.i;
    // JSON number grammar: -?(0|[1-9]\d*)(\.\d+)?([eE][+-]?\d+)?
    const re = /-?(0|[1-9]\d*)(\.\d+)?([eE][+-]?\d+)?/y;
    re.lastIndex = this.i;
    const m = re.exec(this.s);
    if (!m || m.index !== this.i || m[0].length === 0) {
      throw this.err('Invalid number');
    }
    this.i += m[0].length;
    return { type: 'number', raw: m[0], start, end: this.i };
  }

  private parseLiteral(): LiteralNode {
    const start = this.i;
    if (this.s.startsWith('true', this.i)) {
      this.i += 4;
      return { type: 'literal', value: true, raw: 'true', start, end: this.i };
    }
    if (this.s.startsWith('false', this.i)) {
      this.i += 5;
      return { type: 'literal', value: false, raw: 'false', start, end: this.i };
    }
    if (this.s.startsWith('null', this.i)) {
      this.i += 4;
      return { type: 'literal', value: null, raw: 'null', start, end: this.i };
    }
    throw this.err(`Unexpected token, expected 'true', 'false' or 'null'`);
  }

  private skipWs(): void {
    while (this.i < this.s.length && WS.has(this.s.charCodeAt(this.i))) {
      this.i++;
    }
  }

  private err(message: string): JsonSyntaxError {
    return this.errAt(this.i, message);
  }
  private errAt(offset: number, message: string): JsonSyntaxError {
    return new JsonSyntaxError(message, offsetToPos(this.s, offset));
  }
}

/** Parse JSON text into a lossless AST. Throws {@link JsonSyntaxError}. */
export function parse(text: string): JsonNode {
  return new Parser(text).parse();
}

// --- Serialization ----------------------------------------------------------

export interface FormatOptions {
  /** Number of spaces per indent level, or a literal indent string. */
  indent?: number | string;
}

function indentString(opt: number | string | undefined, fallback = 2): string {
  if (opt === undefined) return ' '.repeat(fallback);
  if (typeof opt === 'number') return ' '.repeat(Math.max(0, opt));
  return opt;
}

function serialize(node: JsonNode, pad: string, level: number): string {
  switch (node.type) {
    case 'string':
      return node.raw;
    case 'number':
      return node.raw;
    case 'literal':
      return node.raw;
    case 'array': {
      if (node.elements.length === 0) return '[]';
      const inner = pad.repeat(level + 1);
      const nl = pad === '' ? '' : '\n';
      const items = node.elements.map(
        (el) => inner + serialize(el, pad, level + 1),
      );
      return `[${nl}${items.join(`,${nl}`)}${nl}${pad.repeat(level)}]`;
    }
    case 'object': {
      if (node.members.length === 0) return '{}';
      const inner = pad.repeat(level + 1);
      const nl = pad === '' ? '' : '\n';
      const sep = pad === '' ? ':' : ': ';
      const items = node.members.map(
        (m) => inner + m.keyRaw + sep + serialize(m.value, pad, level + 1),
      );
      return `{${nl}${items.join(`,${nl}`)}${nl}${pad.repeat(level)}}`;
    }
  }
}

/** Pretty-print JSON, preserving exact scalar tokens and duplicate keys. */
export function format(text: string, options: FormatOptions = {}): string {
  const node = parse(text);
  return serialize(node, indentString(options.indent, 2), 0);
}

/** Minify JSON to the most compact form, preserving exact scalar tokens. */
export function minify(text: string): string {
  const node = parse(text);
  return serialize(node, '', 0);
}

// --- Validation -------------------------------------------------------------

export interface ValidationIssue {
  message: string;
  pos: Pos;
}

export interface ValidationResult {
  ok: boolean;
  /** The fatal syntax error, if the text is not valid JSON. */
  error?: ValidationIssue;
  /** Non-fatal advisories (duplicate keys, unsafe integers, etc.). */
  warnings: ValidationIssue[];
}

const MAX_SAFE = BigInt(Number.MAX_SAFE_INTEGER);
const MIN_SAFE = BigInt(Number.MIN_SAFE_INTEGER);

function collectWarnings(
  node: JsonNode,
  text: string,
  out: ValidationIssue[],
): void {
  switch (node.type) {
    case 'object': {
      const seen = new Map<string, number>();
      for (const m of node.members) {
        if (seen.has(m.key)) {
          out.push({
            message: `Duplicate key ${JSON.stringify(m.key)} - later value overrides the earlier one`,
            pos: offsetToPos(text, m.keyStart),
          });
        } else {
          seen.set(m.key, m.keyStart);
        }
        collectWarnings(m.value, text, out);
      }
      break;
    }
    case 'array':
      for (const el of node.elements) collectWarnings(el, text, out);
      break;
    case 'number': {
      // Integer literal outside the IEEE-754 safe range loses precision when
      // consumed by JS (or any JSON.parse-based tool).
      if (/^-?\d+$/.test(node.raw)) {
        const v = BigInt(node.raw);
        if (v > MAX_SAFE || v < MIN_SAFE) {
          out.push({
            message: `Integer ${node.raw} exceeds the safe range and may lose precision when parsed`,
            pos: offsetToPos(text, node.start),
          });
        }
      }
      break;
    }
    default:
      break;
  }
}

/**
 * Validate JSON text. Returns a fatal syntax error (with precise position) if
 * present, plus non-fatal warnings for duplicate keys and unsafe integers.
 * An empty / whitespace-only input is treated as "not yet valid" without an
 * error, so the UI can stay quiet until the user types something.
 */
export function validate(text: string): ValidationResult {
  const warnings: ValidationIssue[] = [];
  if (text.trim() === '') {
    return { ok: false, warnings };
  }
  let node: JsonNode;
  try {
    node = parse(text);
  } catch (e) {
    if (e instanceof JsonSyntaxError) {
      return { ok: false, error: { message: e.message, pos: e.pos }, warnings };
    }
    throw e;
  }
  collectWarnings(node, text, warnings);
  return { ok: true, warnings };
}

// --- Shared node utilities (used by tree view and structural diff) ----------

/**
 * Canonicalize a JSON number literal to a lossless decimal form so equality can
 * be decided WITHOUT converting to a JS `number` (which would round large or
 * high-precision integers - exactly what this tool must not do).
 *
 * The form is `[-]<digits>e<exp>` with no leading/trailing insignificant zeros,
 * so `1`, `1.0`, `1e0`, `100e-2` all canonicalize equal, while
 * `9007199254740992` and `9007199254740993` stay distinct. Signed zero is
 * preserved (`-0` !== `0`) to stay faithful to the source.
 */
export function canonicalNumber(raw: string): string {
  const m = /^(-)?(\d+)(?:\.(\d+))?(?:[eE]([+-]?\d+))?$/.exec(raw);
  if (!m) return raw;
  const neg = m[1] === '-';
  const fracPart = m[3] ?? '';
  // BigInt so exponents beyond Number.MAX_SAFE_INTEGER stay exact.
  let exp = m[4] ? BigInt(m[4]) : 0n;
  let digits = m[2] + fracPart;
  exp -= BigInt(fracPart.length);
  digits = digits.replace(/^0+/, '');
  const withoutTrailing = digits.replace(/0+$/, '');
  exp += BigInt(digits.length - withoutTrailing.length);
  digits = withoutTrailing;
  if (digits === '') return neg ? '-0' : '0';
  return `${neg ? '-' : ''}${digits}e${exp.toString()}`;
}

/** Structural equality for two scalar nodes (string/number/literal). */
export function scalarEqual(a: JsonNode, b: JsonNode): boolean {
  if (a.type !== b.type) return false;
  if (a.type === 'string' && b.type === 'string') return a.value === b.value;
  if (a.type === 'number' && b.type === 'number') {
    return canonicalNumber(a.raw) === canonicalNumber(b.raw);
  }
  if (a.type === 'literal' && b.type === 'literal') return a.value === b.value;
  return false;
}

/** Count all nodes in a tree (iterative, so deep structures can't overflow). */
export function countNodes(root: JsonNode): number {
  let count = 0;
  const stack: JsonNode[] = [root];
  while (stack.length > 0) {
    const n = stack.pop()!;
    count++;
    if (n.type === 'object') {
      for (const m of n.members) stack.push(m.value);
    } else if (n.type === 'array') {
      for (const el of n.elements) stack.push(el);
    }
  }
  return count;
}

const IDENTIFIER = /^[A-Za-z_$][A-Za-z0-9_$]*$/;

/** Format one object key as a JSONPath segment, escaping non-identifiers. */
export function pathKeySegment(key: string): string {
  return IDENTIFIER.test(key) ? `.${key}` : `[${JSON.stringify(key)}]`;
}

const PREVIEW_MAX = 200;

/** A bounded, human-readable preview of a node's value. */
export function nodePreview(node: JsonNode): string {
  switch (node.type) {
    case 'string':
      return node.raw.length > PREVIEW_MAX
        ? node.raw.slice(0, PREVIEW_MAX) + '…"'
        : node.raw;
    case 'number':
      return node.raw;
    case 'literal':
      return node.raw;
    case 'array':
      return `[…] ${node.elements.length} ${node.elements.length === 1 ? 'item' : 'items'}`;
    case 'object':
      return `{…} ${node.members.length} ${node.members.length === 1 ? 'key' : 'keys'}`;
  }
}
